import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Eye,
  FileCode,
  FileText,
  Flag,
  FolderInput,
  Forward,
  ImageOff,
  Mail,
  MailOpen,
  MessagesSquare,
  OctagonAlert,
  Reply,
  ReplyAll,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import { MailRahmen } from "./MailRahmen";
import { Unterhaltung } from "./Unterhaltung";
import { Anhaenge } from "./Anhaenge";
import { Avatar } from "./Avatar";
import {
  api,
  categoryColor,
  CATEGORIES,
  type ActionRow,
  type Folder,
  type MessageDetail,
  type PromptPreview,
} from "../api";
import { Select } from "./Select";
import { datumKurz, datumLang } from "../lib/datum";
import { ordnerOptionen, ordnerPfad } from "../lib/ordner";
import { toast } from "./ui/Toast";
import { haptik } from "../lib/mobil";

interface Props {
  message: MessageDetail | null;
  folders: Folder[];
  onChanged: () => void;
  onReply: (messageId: number, kind: "antwort" | "allen" | "weiterleiten", draftId?: number) => void;
  onBack: () => void;
  active: boolean;
  /** Die gewählte Nachricht wird gerade geladen. */
  laedt?: boolean;
  /** Laden fehlgeschlagen. */
  fehler?: string | null;
  onRetry?: () => void;
  /** Nach Verschieben, Papierkorb, Spam: Die Nachricht verlässt die aktuelle Ansicht. */
  onEntfernt?: (id: number) => void;
  /** Eine andere Nachricht öffnen, z. B. aus dem Verlauf. */
  onOpen?: (id: number) => void;
  /** Gelesen-Status setzen — zeigt die Änderung sofort, auch in den Ordnerzahlen. */
  onGelesen?: (id: number, gelesen: boolean) => Promise<void>;
  /** Braucht keine Antwort — verlässt „Zu beantworten". */
  onErledigt?: (id: number) => void;
}

/** Was das Absender-Gedächtnis über diesen Kontakt weiß. */
function reputationLabel(message: MessageDetail): string {
  const rep = message.senderReputation?.address ?? message.senderReputation?.domain;
  if (!rep) return "Dieser Absender ist neu.";
  if (rep.manual === "ham") return "Von dir als vertrauenswürdig festgelegt.";
  if (rep.manual === "spam") return "Von dir blockiert.";
  const parts: string[] = [];
  if (rep.replied) parts.push("du hast schon geantwortet");
  if (rep.hamCount > 0) parts.push(`${rep.hamCount}× erwünscht`);
  if (rep.spamCount > 0) parts.push(`${rep.spamCount}× als Spam eingestuft`);
  return parts.length > 0 ? `Bekannt: ${parts.join(", ")}.` : "Dieser Absender ist neu.";
}

function payloadVon(a: ActionRow): { folder?: string } {
  try {
    return JSON.parse(a.payload_json || "{}") as { folder?: string };
  } catch {
    return {};
  }
}

function actionLabel(a: ActionRow, folders: Folder[]): string {
  const payload = payloadVon(a);
  const ziel = payload.folder ? folders.find((f) => f.path === payload.folder) : undefined;
  const folderName = ziel ? ordnerPfad(ziel, folders) : payload.folder ?? "";
  switch (a.type) {
    case "move": return `nach „${folderName}" verschieben`;
    case "copy": return `nach „${folderName}" kopieren`;
    case "markSpam": return `als unerwünscht nach „${folderName}" verschieben`;
    case "flag": return "als wichtig markieren";
    case "markRead": return "als gelesen markieren";
    case "delete": return "löschen";
    default: return a.type;
  }
}

export function Reader({ message, folders, onChanged, onReply, onBack, active, laedt, fehler, onRetry, onEntfernt, onOpen, onGelesen, onErledigt }: Props) {
  const [busy, setBusy] = useState(false);
  const [showHtml, setShowHtml] = useState(true);
  const [correcting, setCorrecting] = useState(false);
  const [showSignals, setShowSignals] = useState(false);
  const [prompt, setPrompt] = useState<PromptPreview | null>(null);
  const [promptLaedt, setPromptLaedt] = useState(false);
  const [bilderFrei, setBilderFrei] = useState(false);
  const [empfaengerOffen, setEmpfaengerOffen] = useState(false);
  const scroller = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setShowHtml(true);
    setCorrecting(false);
    setShowSignals(false);
    setPrompt(null);
    setBilderFrei(false);
    setEmpfaengerOffen(false);
    // Neue Nachricht beginnt oben, nicht an der Scrollposition der vorigen.
    scroller.current?.scrollTo({ top: 0 });
  }, [message?.id]);

  if (!message) {
    return (
      <section className="pane reader" data-active={active} aria-label="Nachricht">
        {(laedt || fehler) && (
          <header className="pane-head reader-kopf only-narrow-flex">
            <button type="button" className="icon-btn" onClick={onBack} title="Zurück zur Liste" aria-label="Zurück zur Liste">
              <ArrowLeft aria-hidden="true" />
            </button>
          </header>
        )}
        {fehler ? (
          <div className="empty" role="alert">
            <strong>Nachricht konnte nicht geladen werden</strong>
            <span>{fehler}</span>
            {onRetry && <button type="button" className="btn" style={{ marginTop: 10 }} onClick={onRetry}>Erneut versuchen</button>}
          </div>
        ) : laedt ? (
          <div className="leser-skelett" aria-busy="true" aria-label="Nachricht wird geladen">
            <span style={{ width: "55%", height: 22 }} />
            <span style={{ width: "35%" }} />
            <span style={{ width: "25%" }} />
            <span style={{ width: "100%", height: 180, marginTop: 20 }} />
          </div>
        ) : (
          <div className="empty">
            <Mail className="empty-icon" aria-hidden="true" />
            <strong>Keine Nachricht gewählt</strong>
            <span>Links eine Zeile anklicken, oder mit <kbd>J</kbd> und <kbd>K</kbd> durch die Liste gehen.</span>
          </div>
        )}
      </section>
    );
  }

  const pending = message.actions.filter((a) => a.status === "pending");
  const applied = message.actions.filter((a) => a.status === "applied");
  const rail = categoryColor(message.category);
  const aktuellerOrdner = folders.find((f) => f.id === message.folder_id);
  const ordnerTitel = aktuellerOrdner ? ordnerPfad(aktuellerOrdner, folders) : message.folder_path;
  const empfaenger = [...message.to, ...message.cc.map((c) => `${c} (Kopie)`)];
  const absenderName = message.from_name || message.from_email || "(unbekannt)";

  /**
   * Führt eine Aktion aus und meldet das Ergebnis. `entfernt`: Die Nachricht
   * verlässt die aktuelle Ansicht; der Aufrufer rückt dann zur nächsten vor.
   */
  const run = async (
    fn: () => Promise<unknown>,
    erfolg?: string | ((r: unknown) => { text: string; undo?: () => Promise<unknown> }),
    entfernt = false,
  ) => {
    setBusy(true);
    try {
      const r = await fn();
      if (typeof erfolg === "string") toast.erfolg(erfolg);
      else if (erfolg) {
        const { text, undo } = erfolg(r);
        toast.erfolg(text, undo ? { aktion: { label: "Rückgängig", onClick: async () => { await undo(); onChanged(); } } } : undefined);
      }
      if (entfernt && onEntfernt) onEntfernt(message.id);
      else onChanged();
    } catch (err) {
      toast.fehler(err);
    } finally {
      setBusy(false);
    }
  };

  const aktionId = (r: unknown) => (r as { actionId?: number } | undefined)?.actionId;

  const gelesenUmschalten = () => {
    const text = message.seen ? "Als ungelesen markiert" : "Als gelesen markiert";
    if (!onGelesen) {
      void run(() => api.messageAction(message.id, message.seen ? "markUnread" : "markRead"), text);
      return;
    }
    // Ohne Warten: Der Knopf und die Zahl links springen sofort um.
    onGelesen(message.id, !message.seen)
      .then(() => toast(text))
      .catch((err) => { toast.fehler(err); onChanged(); });
  };

  return (
    <section className="pane reader" data-active={active} aria-label="Nachricht">
      <header className="pane-head reader-kopf">
        <button type="button" className="zurueck only-narrow" onClick={onBack} title="Zurück zur Liste" aria-label="Zurück zur Liste">
          <ArrowLeft aria-hidden="true" /> <span>Zurück</span>
        </button>
        <span className="reader-ordner" title={ordnerTitel}>{ordnerTitel}</span>
        <div className="spacer" />
        <div className="werkzeuge" role="toolbar" aria-label="Aktionen für diese Nachricht">
          <button
            type="button"
            className="btn im-fuss"
            disabled={busy}
            onClick={() => onReply(message.id, "antwort", message.draft_id ?? undefined)}
            title={message.draft_id ? "Vorbereiteten Entwurf öffnen (R)" : "Antworten (R)"}
          >
            <Reply aria-hidden="true" /> <span className="nur-breit">{message.draft_id ? "Entwurf" : "Antworten"}</span>
          </button>
          {(message.to.length + message.cc.length) > 1 && (
            <button type="button" className="icon-btn" disabled={busy} onClick={() => onReply(message.id, "allen")} title="Allen antworten" aria-label="Allen antworten">
              <ReplyAll aria-hidden="true" />
            </button>
          )}
          <button type="button" className="icon-btn im-fuss" disabled={busy} onClick={() => onReply(message.id, "weiterleiten")} title="Weiterleiten (F)" aria-label="Weiterleiten">
            <Forward aria-hidden="true" />
          </button>
          {onErledigt && (message.needs_reply === 1 || Boolean(message.draft_id)) && (
            <button type="button" className="btn quiet" disabled={busy} onClick={() => onErledigt(message.id)}
              title="Erledigt — braucht keine Antwort, etwa telefonisch geklärt (E)">
              <CircleCheck aria-hidden="true" /> <span className="nur-breit">Erledigt</span>
            </button>
          )}
          <span className="werkzeug-trenner" aria-hidden="true" />
          <button
            type="button"
            className="icon-btn im-fuss"
            disabled={busy}
            onClick={gelesenUmschalten}
            title={message.seen ? "Als ungelesen markieren (U)" : "Als gelesen markieren (U)"}
            aria-label={message.seen ? "Als ungelesen markieren" : "Als gelesen markieren"}
          >
            {message.seen ? <Mail aria-hidden="true" /> : <MailOpen aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="icon-btn"
            disabled={busy}
            aria-pressed={message.flagged === 1}
            onClick={() => run(() => api.messageAction(message.id, message.flagged ? "unflag" : "flag"),
              message.flagged ? "Markierung entfernt" : "Als wichtig markiert")}
            title={message.flagged ? "Markierung entfernen" : "Als wichtig markieren"}
            aria-label="Als wichtig markieren"
          >
            <Flag aria-hidden="true" />
          </button>
          <Select
            variant="button"
            value=""
            placeholder="Verschieben …"
            disabled={busy}
            options={ordnerOptionen(folders, message.account_id, message.folder_id)}
            onChange={(v) => {
              if (!v) return;
              const ziel = folders.find((f) => f.account_id === message.account_id && f.path === v);
              void run(
                () => api.messageAction(message.id, "move", v),
                (r) => {
                  const id = aktionId(r);
                  return { text: `Verschoben nach „${ziel ? ordnerPfad(ziel, folders) : v}"`, undo: id ? () => api.undo(id) : undefined };
                },
                true,
              );
            }}
            aria-label="In Ordner verschieben"
            className="reader-verschieben im-fuss"
          />
          <button
            type="button"
            className="icon-btn"
            disabled={busy}
            onClick={() => run(() => api.bulkAction([message.id], "spam"), "Als unerwünscht eingestuft", true)}
            title="Als unerwünscht einstufen"
            aria-label="Als unerwünscht einstufen"
          >
            <OctagonAlert aria-hidden="true" />
          </button>
          <button
            type="button"
            className="icon-btn gefahr im-fuss"
            disabled={busy}
            onClick={() => run(() => api.bulkAction([message.id], "trash"), "In den Papierkorb verschoben", true)}
            title="In den Papierkorb"
            aria-label="In den Papierkorb"
          >
            <Trash2 aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="pane-scroll" ref={scroller}>
        {/* Formatierte Mail braucht mehr Breite als Fließtext: Newsletter
            sind meist auf 600 Pixel gesetzt. */}
        <article className="reader-body" data-weit={Boolean(message.body_html && showHtml)}>
          <h1 className="reader-subject">{message.subject || "(kein Betreff)"}</h1>

          <div className="reader-absender">
            <Avatar email={message.from_email} name={absenderName} groesse="l" className="reader-avatar" />
            <div className="reader-absender-text">
              <div className="reader-from">
                <b title={message.from_email}>{absenderName}</b>
                {message.from_name && <span className="reader-mail">{message.from_email}</span>}
              </div>
              <div className="reader-to">
                {empfaenger.length === 0 ? (
                  "An —"
                ) : empfaenger.length <= 3 || empfaengerOffen ? (
                  <>An {empfaenger.join(", ")}</>
                ) : (
                  <>
                    An {empfaenger.slice(0, 2).join(", ")}{" "}
                    <button type="button" className="link-knopf" onClick={() => setEmpfaengerOffen(true)}>
                      und {empfaenger.length - 2} weitere
                    </button>
                  </>
                )}
              </div>
            </div>
            <time className="reader-datum" dateTime={message.date} title={datumLang(message.date)}>{datumLang(message.date)}</time>
          </div>

          {/* ── Einschätzung des Agenten ───────────────────────────────── */}
          {(message.category || pending.length > 0 || applied.length > 0) && (
            <section className="verdict" style={{ ["--rail-color" as string]: rail }} aria-label="Einschätzung des Agenten">
              <div className="verdict-head">
                <span className="verdict-mark" aria-hidden="true"><Sparkles /></span>
                <span className="verdict-who">Agent</span>
                <div className="verdict-line">
                  {message.category && (
                    <span className="chip solid" style={{ ["--chip-color" as string]: rail }}>
                      {message.category}
                    </span>
                  )}
                  {message.priority && message.priority !== "mittel" && (
                    <span className="chip" style={{ ["--chip-color" as string]: message.priority === "hoch" ? "var(--warn)" : "var(--ink-3)" }}>
                      {message.priority === "hoch" ? "dringend" : "kann warten"}
                    </span>
                  )}
                  {message.needs_reply === 1 && <span className="chip">Antwort erwartet</span>}
                  {message.spam_score !== null && message.spam_score > 0.5 && (
                    <span className="chip" style={{ ["--chip-color" as string]: "var(--cat-spam)" }}>
                      Spam-Verdacht {Math.round(message.spam_score * 100)} %
                    </span>
                  )}
                </div>
                <div className="spacer" />
                <button type="button" className="btn quiet klein" aria-expanded={correcting} onClick={() => setCorrecting((v) => !v)}>
                  Falsch einsortiert?
                </button>
              </div>

              {message.summary && <p className="verdict-summary">{message.summary}</p>}
              {message.reason && <p className="verdict-reason">{message.reason}</p>}

              {Object.keys(message.entities ?? {}).length > 0 && (
                <dl className="verdict-fakten">
                  {Object.entries(message.entities).map(([k, v]) => (
                    <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
                  ))}
                </dl>
              )}

              {correcting && (
                <div className="proposal">
                  <span className="proposal-text">Richtige Kategorie wählen — der Agent merkt sich die Korrektur.</span>
                  <Select
                    style={{ width: 200 }}
                    value=""
                    placeholder="Kategorie …"
                    options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                    aria-label="Richtige Kategorie"
                    onChange={(v) => v && run(async () => {
                      await api.correct(message.id, v);
                      setCorrecting(false);
                    }, `Als „${v}" eingeordnet — der Agent lernt daraus`)}
                  />
                </div>
              )}

              {pending.map((a) => (
                <div className="proposal" key={a.id}>
                  <span className="proposal-text">
                    Vorschlag: <b>{actionLabel(a, folders)}</b>
                    <span className="proposal-quelle">
                      {a.rule_name ? `Regel „${a.rule_name}"` : a.source === "llm" ? `Sicherheit ${Math.round(a.confidence * 100)} %` : ""}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={busy}
                    onClick={() => run(() => api.approve(a.id), () => ({ text: "Vorschlag übernommen", undo: () => api.undo(a.id) }))}
                  >
                    Übernehmen <kbd>A</kbd>
                  </button>
                  <button type="button" className="btn" disabled={busy} onClick={() => run(() => api.reject(a.id), "Vorschlag verworfen")}>
                    Verwerfen <kbd>X</kbd>
                  </button>
                </div>
              ))}

              {applied.map((a) => (
                <div className="proposal done" key={a.id}>
                  <span className="proposal-text">Erledigt: {actionLabel(a, folders)}</span>
                  <button type="button" className="btn quiet" disabled={busy} onClick={() => run(() => api.undo(a.id), "Rückgängig gemacht")}>
                    Rückgängig
                  </button>
                </div>
              ))}

              {/* Befunde der technischen Prüfung — aufklappbar, damit sie den
                  Normalfall nicht zumüllen, aber im Zweifel nachvollziehbar sind. */}
              {message.heuristic?.signals?.length > 0 && (
                <div className="signals">
                  <button type="button" className="signals-toggle" aria-expanded={showSignals} onClick={() => setShowSignals((v) => !v)}>
                    {showSignals ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                    <ShieldCheck aria-hidden="true" />
                    Technische Prüfung
                    {message.heuristic.score !== null && ` · Punktzahl ${message.heuristic.score}`}
                    {` · ${message.heuristic.signals.length} ${message.heuristic.signals.length === 1 ? "Befund" : "Befunde"}`}
                  </button>

                  {showSignals && (
                    <>
                      <div className="auth-row">
                        {(["spf", "dkim", "dmarc"] as const).map((k) => (
                          <span
                            key={k}
                            className="chip"
                            style={{
                              ["--chip-color" as string]:
                                message.heuristic[k] === "pass" ? "var(--ok)"
                                : message.heuristic[k] === "fail" ? "var(--warn)"
                                : "var(--ink-3)",
                            }}
                          >
                            {k.toUpperCase()}: {message.heuristic[k] ?? "nicht geprüft"}
                          </span>
                        ))}
                      </div>

                      <ul className="signal-list">
                        {message.heuristic.signals.map((sig) => (
                          <li key={sig.id} data-kind={sig.weight > 0 ? "verdacht" : "entlastung"}>
                            <span className="signal-label">{sig.label}</span>
                            {sig.detail && <span className="signal-detail">{sig.detail}</span>}
                          </li>
                        ))}
                      </ul>

                      <div className="signal-actions">
                        <span className="signal-detail" style={{ flex: 1 }}>
                          {reputationLabel(message)}
                        </span>
                        <button type="button" className="btn quiet klein" disabled={busy}
                          onClick={() => run(() => api.markSender(message.id, "ham"), "Absender als vertrauenswürdig gemerkt")}>
                          Absender vertrauen
                        </button>
                        <button type="button" className="btn quiet klein" disabled={busy}
                          onClick={() => run(() => api.markSender(message.id, "spam"), "Absender blockiert")}>
                          Absender blockieren
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          <Anhaenge messageId={message.id} attachments={message.attachments} />

          {message.thread.length > 1 && <Unterhaltung messageId={message.id} onOpen={onOpen} />}

          {message.body_html && showHtml && !bilderFrei && /data-blockiert/.test(message.body_html) && (
            <div className="bilder-hinweis">
              <ImageOff aria-hidden="true" />
              <span>
                Externe Bilder wurden nicht geladen — sie verraten dem Absender, wann du die Mail öffnest.
              </span>
              <button type="button" className="btn klein" onClick={() => setBilderFrei(true)}>Bilder anzeigen</button>
            </div>
          )}

          <div className="reader-content">
            {message.body_html && showHtml ? (
              <MailRahmen html={message.body_html} bilderFrei={bilderFrei} />
            ) : message.body_text?.trim() ? (
              <pre>{message.body_text}</pre>
            ) : (
              <p className="hint">(kein Textinhalt)</p>
            )}
          </div>

          <div className="reader-fuss">
            {message.body_html && (
              <button type="button" className="btn quiet klein" onClick={() => setShowHtml((v) => !v)}>
                {showHtml ? <FileText aria-hidden="true" /> : <FileCode aria-hidden="true" />}
                {showHtml ? "Als reinen Text anzeigen" : "Formatiert anzeigen"}
              </button>
            )}
            <button
              type="button"
              className="btn quiet klein"
              aria-expanded={Boolean(prompt)}
              disabled={promptLaedt}
              onClick={async () => {
                if (prompt) return setPrompt(null);
                setPromptLaedt(true);
                try {
                  setPrompt(await api.messagePrompt(message.id));
                } catch (err) {
                  toast.fehler(err);
                } finally {
                  setPromptLaedt(false);
                }
              }}
            >
              <Eye aria-hidden="true" />
              {promptLaedt ? "Lade …" : prompt ? "Übermittlung ausblenden" : "Was geht an das Modell?"}
            </button>
          </div>

          {prompt && (
            <div className="prompt-preview">
              {prompt.ausgeschlossen ? (
                <p className="ok-line" style={{ margin: 0 }}>
                  Der Ordner „{prompt.ordner}" ist ausgeschlossen — von dieser Mail geht nichts an ein Modell.
                </p>
              ) : (
                <>
                  <div className="prompt-head">
                    <span className="chip">{prompt.zeichen} Zeichen</span>
                    <span className="chip">Schwärzung: {prompt.stufe}</span>
                    <span
                      className="chip"
                      style={{ ["--chip-color" as string]: Object.keys(prompt.geschwaerzt).length ? "var(--ok)" : "var(--ink-3)" }}
                    >
                      {prompt.beschreibung}
                    </span>
                    {prompt.bereitsGesendet && (
                      <span className="chip">
                        gesendet an {prompt.bereitsGesendet.model}
                      </span>
                    )}
                  </div>
                  {/* Wortwörtlich derselbe Text, den der Aufruf verschickt. */}
                  <pre className="prompt-text">{prompt.text}</pre>
                </>
              )}
            </div>
          )}
        </article>
      </div>

      {/* Telefon: die häufigsten Aktionen unten, in Daumenreichweite. */}
      <nav className="leser-leiste" aria-label="Aktionen">
        <button type="button" className="leser-aktion haupt" disabled={busy}
          onClick={() => { haptik(); onReply(message.id, "antwort", message.draft_id ?? undefined); }}>
          <Reply aria-hidden="true" /><span>{message.draft_id ? "Entwurf" : "Antworten"}</span>
        </button>
        <button type="button" className="leser-aktion" disabled={busy} onClick={() => onReply(message.id, "weiterleiten")}>
          <Forward aria-hidden="true" /><span>Weiterleiten</span>
        </button>
        <label className="leser-aktion" data-disabled={busy || undefined}>
          <FolderInput aria-hidden="true" /><span>Verschieben</span>
          {/* Natives Auswahlfeld: Das Telefon zeigt dafür seine eigene, große Liste. */}
          <select
            value=""
            disabled={busy}
            aria-label="In Ordner verschieben"
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              const ziel = folders.find((f) => f.account_id === message.account_id && f.path === v);
              void run(
                () => api.messageAction(message.id, "move", v),
                (r) => {
                  const id = aktionId(r);
                  return { text: `Verschoben nach „${ziel ? ordnerPfad(ziel, folders) : v}"`, undo: id ? () => api.undo(id) : undefined };
                },
                true,
              );
            }}
          >
            <option value="" disabled>Verschieben nach …</option>
            {ordnerOptionen(folders, message.account_id, message.folder_id).map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
            ))}
          </select>
        </label>
        <button type="button" className="leser-aktion" disabled={busy}
          onClick={gelesenUmschalten}>
          {message.seen ? <Mail aria-hidden="true" /> : <MailOpen aria-hidden="true" />}
          <span>{message.seen ? "Ungelesen" : "Gelesen"}</span>
        </button>
        <button type="button" className="leser-aktion gefahr" disabled={busy}
          onClick={() => { haptik(); void run(() => api.bulkAction([message.id], "trash"), "In den Papierkorb verschoben", true); }}>
          <Trash2 aria-hidden="true" /><span>Löschen</span>
        </button>
      </nav>
    </section>
  );
}
